// Readable names for tracks and clips.
//
// What Live hands over is whatever the file was called — `001_a__Drum_Loop_
// 87bpm_-_LDTDM_Zenhiser.wav` — plus the suffixes a bounce adds on the way
// through. A track the producer named is already readable and must survive
// untouched; a filename is not, and nobody should have to read one.

import { stripTokens } from "@shared/sync/track-id.js";

/** The tails a bounce leaves on a clip name: "(Bounce)", "(Bounce tail)". */
const BOUNCE_TAIL = /\s*\((?:bounce(?:\s+tail)?)\)\s*$/i;

/**
 * A raw stem or clip name, made readable: no extension, no leading track
 * number, underscores as spaces. Falls back to the original rather than
 * returning nothing.
 */
export function cleanTrackName(raw: string): string {
  return (
    // Stamping bakes a ⟨sb:xxxxxx⟩ id into the Live track name (it is the only
    // durable writable per-track string the SDK has — shared/sync/track-id.ts).
    // It is machinery, not a name: every stamped track would otherwise read
    // "Drums ⟨sb:aea6c9⟩" on every pad and lane header in the app — and a
    // bounced one "Drums ⟨sb:aea6c9⟩ (Bounce)", token in the middle.
    stripTokens(raw)
      .replace(/\.[^.]+$/, "") // drop the extension
      // A lane index and the punctuation joining it to the name: "3-002_Hi_Hat"
      // → "002 Hi Hat", "001_a__Drum" → "a Drum". The separator must be
      // punctuation, never a space — "909 kick" is a name a producer typed and
      // keeps its number. One index only, for the same reason.
      .replace(/^\d+[-–—_.]+\s*/, "")
      .replace(/_/g, " ") // underscores → spaces
      .replace(/\s+/g, " ") // collapse whitespace
      .trim() || raw
  );
}

/**
 * A clip's name for a pad or a lane: `cleanTrackName`, minus the "(Bounce)"
 * a render leaves behind — every clip in a bounced seed carries it, so it
 * distinguishes nothing and costs the width that the name needs.
 */
export function cleanClipName(raw: string): string {
  const withoutTail = raw.replace(BOUNCE_TAIL, "").trim();
  return cleanTrackName(withoutTail || raw);
}
